import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Alert, Button, Typography } from '@mui/material';
import api from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { MAIN_APP_DASHBOARD_URL } from '@/types';
import axios from 'axios';
import { exchangeSsoCode, setSessionFromToken } from '@/utils/ssoExchange';

const isDev = import.meta.env.DEV;

export default function SsoPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const token = params.get('token');
  const code = params.get('code');
  const devLogin = isDev && params.get('dev') === '1';
  const devId = params.get('id');
  const devUsername = params.get('username');
  const devReferenceNo = params.get('reference_no');
  const devRole = params.get('role');
  const devFullName = params.get('full_name');

  useEffect(() => {
    if (!token && !code && !(devLogin && devId)) {
      setError('缺少 SSO 參數');
      return;
    }

    const ac = new AbortController();
    let finished = false;

    (async () => {
      try {
        if (devLogin && devId) {
          const role =
            devRole === 'admin' || devRole === 'developer' || devRole === 'referee'
              ? devRole
              : 'referee';
          await api.post(
            '/auth/dev-login',
            {
              id: Number(devId),
              username: devUsername || devReferenceNo || 'DEV',
              reference_no: devReferenceNo || '',
              role,
              full_name: devFullName || '',
            },
            { timeout: 10000, signal: ac.signal }
          );
        } else if (isDev && token) {
          await setSessionFromToken(token, ac.signal);
        } else if (code) {
          await exchangeSsoCode(code, ac.signal);
        } else {
          setError('缺少 SSO 代碼');
          return;
        }

        if (ac.signal.aborted) return;
        await refresh();
        if (ac.signal.aborted) return;
        finished = true;
        navigate('/dashboard', { replace: true });
      } catch (err: unknown) {
        if (ac.signal.aborted || axios.isCancel(err)) return;
        let message = 'SSO 登入失敗。請返回主系統重新點擊「分析系統」。';
        if (axios.isAxiosError(err)) {
          if (err.code === 'ECONNABORTED') {
            message = isDev
              ? 'SSO 逾時，請確認 data Worker（8787）與 hkhbref 後端（PORT 3002）已啟動。'
              : 'SSO 逾時，請稍後再試或返回主系統重新登入。';
          } else if (err.response?.data?.message) {
            message = String(err.response.data.message);
          } else if (!err.response) {
            message = isDev
              ? '無法連線至分析系統 API（:8787），請確認 npm run dev 的 Worker 已 Ready。'
              : '無法連線至分析系統，請稍後再試。';
          }
        }
        setError(message);
      }
    })();

    return () => {
      if (!finished) ac.abort();
    };
  }, [token, code, devLogin, devId, devUsername, devReferenceNo, devRole, devFullName, navigate, refresh]);

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button variant="contained" color="secondary" href={MAIN_APP_DASHBOARD_URL}>
          返回主系統
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 2 }}>
      <CircularProgress color="secondary" />
      <Typography color="text.secondary">正在登入…</Typography>
      {isDev && (
        <Typography variant="caption" color="text.secondary" textAlign="center">
          本地開發：主系統 :3002 → 本頁 Worker :8787（/api/auth/session-from-token）
        </Typography>
      )}
    </Box>
  );
}
